"use client";

import { useCallback, useMemo } from "react";
import {
  canAccessConnectorScreen,
  hasOtenCopilotAccess,
  normalizeConnectorFeatures,
  normalizeConnectorScreens,
  resolveConnectorCapabilities,
  isReadOnlyRefSystem,
  refSystemAuthMode,
} from "@/lib/connector-access";
import { useActiveWorkspaceContext } from "@/lib/active-workspace";
import { authSelectors, userProfileSelectors } from "@/store/user/slices/auth/selectors";
import { useUserStore } from "@/store/user/user-store";

export function getWorkspaceEntitlementKey(
  workspace:
    | {
        organizationId?: string;
        workspaceId?: string;
      }
    | null
    | undefined,
): string | null {
  if (!workspace?.organizationId || !workspace.workspaceId) return null;
  return `${workspace.organizationId}:${workspace.workspaceId}`;
}

export function useConnectorAccess() {
  const user = useUserStore(userProfileSelectors.userProfile);
  const isAuthLoaded = useUserStore(authSelectors.isLoaded);
  const entitlementsByWorkspace = useUserStore(
    (state) => state.entitlementsByWorkspace,
  );
  const activeEntitlementWorkspaceKey = useUserStore(
    (state) => state.activeEntitlementWorkspaceKey,
  );
  const isEntitlementsLoading = useUserStore(
    (state) => state.isEntitlementsLoading,
  );
  const activeWorkspaceContext = useActiveWorkspaceContext();
  const expectedWorkspaceKey = getWorkspaceEntitlementKey(
    activeWorkspaceContext,
  );
  const hasCurrentWorkspaceEntitlements = Boolean(
    expectedWorkspaceKey &&
      expectedWorkspaceKey === activeEntitlementWorkspaceKey,
  );
  const entitlements = expectedWorkspaceKey
    ? entitlementsByWorkspace[expectedWorkspaceKey]
    : undefined;
  const screens = useMemo(
    () => normalizeConnectorScreens(entitlements?.screens),
    [entitlements?.screens],
  );
  const features = useMemo(
    () => normalizeConnectorFeatures(entitlements?.features),
    [entitlements?.features],
  );
  const capabilities = useMemo(
    () =>
      resolveConnectorCapabilities(
        hasCurrentWorkspaceEntitlements ? entitlements?.screens : undefined,
        hasCurrentWorkspaceEntitlements ? entitlements?.features : undefined,
      ),
    [entitlements?.features, entitlements?.screens, hasCurrentWorkspaceEntitlements],
  );
  const canAccessScreen = useCallback(
    (screen: string) =>
      hasCurrentWorkspaceEntitlements &&
      canAccessConnectorScreen(screens, features, screen),
    [features, hasCurrentWorkspaceEntitlements, screens],
  );

  const isLoading =
    isAuthLoaded === undefined ||
    (isAuthLoaded === true && !user) ||
    !activeWorkspaceContext.workspacesLoaded ||
    !expectedWorkspaceKey ||
    !hasCurrentWorkspaceEntitlements ||
    isEntitlementsLoading;
  const hasConnectorAccess = Object.values(capabilities).some(Boolean);
  const canAccessApp =
    hasCurrentWorkspaceEntitlements && hasOtenCopilotAccess(features);

  return {
    ...capabilities,
    canAccessApp,
    hasConnectorAccess,
    isLoading,
    screens,
    features,
    canAccessScreen,
  };
}

export function useSystemReadOnly(
  system:
    | {
        read_only?: boolean;
        is_template?: boolean;
        is_owner?: boolean;
        has_shared_auth?: boolean;
      }
    | null
    | undefined,
) {
  const readOnly = isReadOnlyRefSystem(system);
  const authMode = refSystemAuthMode(system);
  return { readOnly, authMode };
}
